import React, { useState, useMemo, useCallback } from 'react';

// 🔹 Pojedynczy wiersz desktop
const MiniTableRow = React.memo(({ row, columns }) => {
  return (
    <tr className="hover:bg-primary-50/30 transition-colors">
      {columns.map((col, colIdx) => {
        const content = col.renderCell
          ? col.renderCell({ row, value: row[col.field] })
          : row[col.field];
        return (
          <td
            key={colIdx}
            className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap text-right first:text-left"
          >
            {content}
          </td>
        );
      })}
    </tr>
  );
});

// 🔹 Wersja mobilna
const MiniTableRowMobile = React.memo(({ row, columns }) => {
  return (
    <div className="p-3 bg-white rounded border flex flex-col gap-2">
      {columns.map((col, colIdx) => {
        const content = col.renderCell
          ? col.renderCell({ row, value: row[col.field] })
          : row[col.field];
        return (
          <div key={colIdx} className="flex justify-between text-sm">
            <span className="font-semibold text-gray-500">{col.headerName}:</span>
            <span className="text-gray-900">{content}</span>
          </div>
        );
      })}
    </div>
  );
});

// 🔹 Główny komponent mini-tabeli
export default function MiniTable({ columns, data, title }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = useCallback(
    (col) => {
      if (col.sortable === false) return;
      if (sortField === col.field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(col.field);
        setSortDirection('asc');
      }
    },
    [sortField, sortDirection]
  );

  const sortedData = useMemo(() => {
    if (!sortField) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortField, sortDirection]);

  return (
    <div className="mb-6 overflow-x-auto bg-white rounded-lg shadow p-4">
      {title && (
        <h3 className="font-semibold text-lg mb-2 text-gray-700">{title}</h3>
      )}
      {/* Desktop */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full table-auto divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  onClick={() => handleSort(col)}
                  className={`px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider ${
                    col.sortable !== false ? 'cursor-pointer hover:bg-gray-100' : ''
                  }
                   ${col.align === 'right' ? 'text-right' : 'text-left'}
                  `}
                >
                  <div className={` ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                    {col.headerName}
                    {sortField === col.field && (
                      <span className="text-primary-700 text-sm">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-6 text-center text-gray-500 text-sm"
                >
                  Brak danych
                </td>
              </tr>
            ) : (
              sortedData.map((row, i) => (
                <MiniTableRow key={i} row={row} columns={columns} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="lg:hidden flex flex-col gap-3">
        {sortedData.length === 0 ? (
          <div className="p-3 bg-white rounded border text-center text-gray-500 text-sm">
            Brak danych
          </div>
        ) : (
          sortedData.map((row, i) => (
            <MiniTableRowMobile key={i} row={row} columns={columns} />
          ))
        )}
      </div>
    </div>
  );
}
